import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { patientAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Plus, ChevronRight, User, Stethoscope, Archive, ArchiveRestore, X, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const FILTER_LABELS = {
  'mit-wunden': { label: 'Mit aktiven Wunden', icon: Stethoscope, color: 'bg-rose-50 text-rose-700 border-rose-200' },
  'faellige-vw': { label: 'Fällige Verbandswechsel', icon: AlertTriangle, color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

export default function PatientListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canArchive } = useAuth();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [archivedPatients, setArchivedPatients] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('aktiv');
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || '');

  useEffect(() => { loadPatients(); }, [search]);
  useEffect(() => { if (canArchive) loadArchivedCount(); }, []);
  useEffect(() => { if (tab === 'archiv' && canArchive) loadArchived(); }, [tab]);
  useEffect(() => { 
    const filter = searchParams.get('filter') || '';
    setActiveFilter(filter);
  }, [searchParams]);
  useEffect(() => { applyFilter(); }, [patients, activeFilter]);

  const loadPatients = async () => {
    try {
      const res = await patientAPI.list({ search });
      setPatients(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const applyFilter = () => {
    if (!activeFilter) {
      setFilteredPatients(patients);
      return;
    }
    
    let filtered = patients;
    if (activeFilter === 'mit-wunden') {
      filtered = patients.filter(p => (p.wound_count || 0) > 0);
    } else if (activeFilter === 'faellige-vw') {
      filtered = patients.filter(p => p.has_due_vw === true);
    }
    setFilteredPatients(filtered);
  };

  const clearFilter = () => {
    setSearchParams({});
    setActiveFilter('');
  };

  const loadArchivedCount = async () => {
    try {
      const res = await patientAPI.archived();
      setArchivedCount(res.data.length);
    } catch { /* no access */ }
  };

  const loadArchived = async () => {
    try {
      const res = await patientAPI.archived();
      setArchivedPatients(res.data);
      setArchivedCount(res.data.length);
    } catch { /* no archived */ }
  };

  const handleRestore = async (e, patientId) => {
    e.stopPropagation();
    try {
      await patientAPI.restore(patientId);
      toast.success('Patient wiederhergestellt');
      loadArchived();
      loadPatients();
      loadArchivedCount();
    } catch { toast.error('Fehler'); }
  };

  const calcAge = (dob) => {
    if (!dob) return '';
    return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const PatientCard = ({ p, showRestore }) => (
    <Card
      key={p.id}
      className={`border shadow-sm hover:shadow-md transition-all cursor-pointer ${p.status === 'archiviert' ? 'border-slate-300 opacity-75' : 'border-slate-200 hover:border-teal-200'}`}
      onClick={() => navigate(`/patienten/${p.id}`)}
      data-testid={`patient-card-${p.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${p.status === 'archiviert' ? 'bg-slate-100 text-slate-500' : 'bg-teal-50 text-teal-700'}`}>
            {p.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-900 truncate">{p.name}</p>
              {p.status === 'archiviert' && <Badge variant="outline" className="border text-[10px] bg-slate-100 text-slate-500 border-slate-200">Archiviert</Badge>}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
              <span>{calcAge(p.geburtsdatum)} J.</span>
              {p.pflegegrad > 0 && <span>PG {p.pflegegrad}</span>}
              {p.wound_count > 0 && (
                <span className="flex items-center gap-1"><Stethoscope className="w-3 h-3" />{p.wound_count} {p.wound_count === 1 ? 'Wunde' : 'Wunden'}</span>
              )}
            </div>
          </div>
          {showRestore ? (
            <Button variant="outline" size="sm" onClick={(e) => handleRestore(e, p.id)} className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 shrink-0" data-testid={`restore-btn-${p.id}`}>
              <ArchiveRestore className="w-3 h-3 mr-1" />Wiederherstellen
            </Button>
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4" data-testid="patient-list-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 tracking-tight">Patienten</h1>
        <Button data-testid="add-patient-btn" onClick={() => navigate('/patienten/neu')} className="h-11 bg-teal-700 hover:bg-teal-800 text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Neuer Patient</span><span className="sm:hidden">Neu</span>
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="patient-search-input" placeholder="Patient suchen..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11 rounded-xl" />
      </div>

      {/* Active Filter Badge */}
      {activeFilter && FILTER_LABELS[activeFilter] && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`px-3 py-1.5 text-sm ${FILTER_LABELS[activeFilter].color}`}>
            {React.createElement(FILTER_LABELS[activeFilter].icon, { className: "w-3.5 h-3.5 mr-1.5 inline" })}
            {FILTER_LABELS[activeFilter].label}
            <span className="ml-2 font-bold">({filteredPatients.length})</span>
          </Badge>
          <Button variant="ghost" size="sm" onClick={clearFilter} className="h-8 px-2 text-slate-500 hover:text-slate-700">
            <X className="w-4 h-4" />
            <span className="ml-1 text-xs">Filter entfernen</span>
          </Button>
        </div>
      )}

      {canArchive ? (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full bg-slate-100 rounded-xl p-1 h-auto">
            <TabsTrigger value="aktiv" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-aktiv">
              Aktiv ({activeFilter ? filteredPatients.length : patients.length})
            </TabsTrigger>
            <TabsTrigger value="archiv" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-archiv">
              <Archive className="w-3.5 h-3.5 mr-1.5" />Archiv ({archivedCount})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="aktiv" className="mt-4 space-y-2">
            {loading ? [1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />) :
              filteredPatients.length === 0 ? (
                <div className="text-center py-12 text-slate-400"><User className="w-10 h-10 mx-auto mb-3 opacity-50" /><p className="font-medium">{activeFilter ? 'Keine Patienten mit diesem Filter' : 'Keine Patienten gefunden'}</p></div>
              ) : filteredPatients.map(p => <PatientCard key={p.id} p={p} showRestore={false} />)
            }
          </TabsContent>
          <TabsContent value="archiv" className="mt-4 space-y-2">
            {archivedPatients.length === 0 ? (
              <div className="text-center py-12 text-slate-400"><Archive className="w-10 h-10 mx-auto mb-3 opacity-50" /><p className="font-medium">Keine archivierten Patienten</p></div>
            ) : archivedPatients.map(p => <PatientCard key={p.id} p={p} showRestore={true} />)
            }
          </TabsContent>
        </Tabs>
      ) : (
        <div className="space-y-2">
          {loading ? [1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />) :
            filteredPatients.length === 0 ? (
              <div className="text-center py-12 text-slate-400"><User className="w-10 h-10 mx-auto mb-3 opacity-50" /><p className="font-medium">{activeFilter ? 'Keine Patienten mit diesem Filter' : 'Keine Patienten gefunden'}</p></div>
            ) : filteredPatients.map(p => <PatientCard key={p.id} p={p} showRestore={false} />)
          }
        </div>
      )}
    </div>
  );
}
